import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAccommodationBookings } from '../api/admin';
import { useCountry } from '../context/CountryContext';
import api from '../api/client';
import BookingSettlementPanel from '../components/BookingSettlementPanel';

const STATUSES = [
  { key: '', label: 'All statuses' },
  { key: 'requested', label: 'Request sent' },
  { key: 'pending', label: 'Awaiting payment' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'checked_in', label: 'Checked in' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'declined', label: 'Declined by host' },
  { key: 'expired', label: 'Expired' },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.filter((st) => st.key).map((st) => [st.key, st.label]));
const lastDay = (b) => {
  if (b.booking_unit === 'slot') return `${b.check_in} · ${b.slot_label || 'time slot'}`;
  if (b.booking_unit === 'day') {
    const d = new Date(`${b.check_out}T00:00:00Z`); d.setUTCDate(d.getUTCDate() - 1);
    const last = d.toISOString().slice(0, 10);
    return last === b.check_in ? b.check_in : `${b.check_in} → ${last}`;
  }
  return `${b.check_in} → ${b.check_out}`;
};

const money = (cents, country) => {
  const amount = ((Number(cents) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return country === 'KE' ? `KES ${amount}` : `$${amount}`;
};

// Accommodation Bookings (Kayd, 19.10.2026): a booking belongs to its
// listing's country. The list follows the country selected in the header —
// enforced on the server, also for restricted staff — and shows a clear
// empty state. The status filter lives in the URL, so it survives a refresh.
const AccommodationBookings = () => {
  const { country, availableOptions, ready: countryReady } = useCountry();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = STATUSES.some((st) => st.key === searchParams.get('status')) ? searchParams.get('status') : '';
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestSeq = useRef(0);
  const [openId, setOpenId] = useState(null);
  const [reload, setReload] = useState(0);
  // Refunds waiting for a decision, a cash confirmation or a retry.
  const [actionRefunds, setActionRefunds] = useState([]);
  useEffect(() => {
    if (!countryReady) return;
    const params = country && country !== 'ALL' && country !== 'NONE' ? { country } : {};
    api.get('/api/rooms/admin/refunds', { params }).then((r) => setActionRefunds(r.data?.refunds || [])).catch(() => setActionRefunds([]));
  }, [country, countryReady, reload]);

  useEffect(() => {
    if (!countryReady) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (country && country !== 'ALL' && country !== 'NONE') params.country = country;
    getAccommodationBookings(params)
      .then((res) => { if (seq === requestSeq.current) { setBookings(res.data?.bookings || []); setError(''); } })
      .catch((e) => { if (seq === requestSeq.current) { setBookings([]); setError(e.response?.data?.error || 'Could not load bookings.'); } })
      .finally(() => { if (seq === requestSeq.current) setLoading(false); });
  }, [statusFilter, country, countryReady, reload]);

  const setStatusFilter = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set('status', v); else next.delete('status');
    setSearchParams(next, { replace: true });
  };

  const countryName = country === 'ALL' ? 'all countries' : (availableOptions.find((o) => o.code === country)?.label || country);
  const statusName = STATUSES.find((st) => st.key === statusFilter)?.label.toLowerCase();

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Accommodation Bookings</h1>
      <select style={s.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status">
        {STATUSES.map((st) => <option key={st.key} value={st.key}>{st.label}</option>)}
      </select>

      <p style={s.summary} aria-live="polite">
        {loading ? 'Loading…' : `${bookings.length} booking${bookings.length === 1 ? '' : 's'}${statusFilter ? ` (${statusName})` : ''} · ${countryName}`}
      </p>
      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
      {actionRefunds.length > 0 && (
        <div style={s.warnBox} role="alert">
          <strong>Refunds needing action ({actionRefunds.length})</strong>
          <ul style={s.ul}>
            {actionRefunds.map((r) => (
              <li key={r.id}>
                {r.refund_number} · {r.booking?.listing?.title || 'booking'} · {money(r.amount_cents, r.booking?.listing?.country)} · {r.method} ·{' '}
                <strong>{{ under_review: 'under review — decision needed', processing: r.method === 'cash' ? 'waiting for cash repayment confirmation' : 'waiting for the payment provider', failed: 'FAILED — try again' }[r.status] || r.status}</strong>
                {' '}<button type="button" style={s.linkBtn} onClick={() => setOpenId(r.booking_id)}>Open</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!loading && !error && bookings.length === 0 && (
        <div style={s.empty}>
          No {statusFilter ? `${statusName} ` : ''}bookings in {countryName}.
          {country !== 'ALL' && <div style={s.emptyHint}>Choose "All countries" or another country in the header to see more.</div>}
        </div>
      )}

      {!loading && bookings.length > 0 && (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Listing</th><th style={s.th}>Country</th><th style={s.th}>Guest</th>
              <th style={s.th}>Dates</th><th style={s.th}>Total</th><th style={s.th}>Payment</th><th style={s.th}>Status</th><th style={s.th} />
            </tr></thead>
            <tbody>
              {bookings.map((b) => (
                <React.Fragment key={b.id}>
                <tr>
                  <td style={s.td}>{b.listing?.title}</td>
                  <td style={s.td}>{b.listing?.country || '—'}</td>
                  <td style={s.td}>
                    {b.guest?.full_name || b.guest?.email}
                    {b.guest?.customer_number && <div style={s.muted}>#{b.guest.customer_number}</div>}
                  </td>
                  <td style={s.td}>{lastDay(b)}</td>
                  <td style={s.td}>{money(b.total_price_cents, b.listing?.country)}</td>
                  <td style={s.td}>
                    {b.payment_method === 'cash' ? '💵 Cash on arrival' : '💳 Online'}
                    {b.settlement?.payment && <div style={s.muted}>{b.settlement.payment.label}</div>}
                    {b.settlement?.fee?.ref && <div style={s.muted}>{b.settlement.fee.ref}: {b.settlement.fee.label}</div>}
                  </td>
                  <td style={s.td}>
                    <span style={s.badge}>{STATUS_LABEL[b.status] || b.status}</span>
                    {b.cancellation_reason && <div style={s.muted}>{b.cancellation_reason.replace(/_/g, ' ')}</div>}
                    {b.settlement?.refund && <div style={s.muted}>Refund: {b.settlement.refund.label}</div>}
                  </td>
                  <td style={s.td}><button type="button" style={s.linkBtn} aria-expanded={openId === b.id} onClick={() => setOpenId(openId === b.id ? null : b.id)}>{openId === b.id ? 'Close' : 'Details'}</button></td>
                </tr>
                {openId === b.id && (
                  <tr><td colSpan={8} style={s.tdPanel}><BookingSettlementPanel booking={b} onChanged={() => setReload((n) => n + 1)} /></td></tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const s = {
  page: { padding: 24, maxWidth: 1100 },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 16 },
  select: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 6 },
  summary: { fontSize: 13, color: '#475569', margin: '6px 0 10px', fontWeight: 600 },
  empty: { background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12, padding: '20px 16px', color: '#475569', fontSize: 14, textAlign: 'center' },
  emptyHint: { fontSize: 12.5, color: '#64748b', marginTop: 6 },
  tableWrap: { width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  table: { width: '100%', minWidth: 900, borderCollapse: 'collapse', background: 'white', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  muted: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  warnBox: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 10, padding: 12, fontSize: 13.5, marginBottom: 12, lineHeight: 1.5 },
  ul: { margin: '6px 0 0', paddingLeft: 18 },
  linkBtn: { background: 'none', border: 'none', color: '#0B6A4A', fontWeight: 700, cursor: 'pointer', padding: '2px 4px', fontSize: 13 },
  tdPanel: { padding: '6px 10px 14px', borderBottom: '1px solid #e5e7eb' },
  badge: { padding: '3px 8px', borderRadius: 999, background: '#f3f4f6', fontSize: 11, fontWeight: 700 },
};

export default AccommodationBookings;
